import React, { useCallback, useEffect, useState } from 'react'
import { createAPI } from '@/utils/modal'
import { useInViewport } from 'ahooks'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import styles from './index.less'
import classNames from 'classnames'
import { setCookiesStorage, checkCookiesStorage } from '@/utils/localStorage'
import { getRootDom } from '@/utils/utils'
import { register } from '@/utils/scheduler'
import { captureException } from '@/utils/track'

const _GuideDestroyFns = []

const VIEW_PORT_THRESHOLD_025 = 0.25
const VIEW_PORT_THRESHOLD_05 = 0.5
const VIEW_PORT_THRESHOLD_075 = 0.75
const inViewportThreshold = [0, VIEW_PORT_THRESHOLD_025, VIEW_PORT_THRESHOLD_05, VIEW_PORT_THRESHOLD_075, 1]
const ratioLimit = 1

export const GuideDestroy = () => {
    while (_GuideDestroyFns.length) {
        let destroy = _GuideDestroyFns.pop()

        if (destroy) {
            destroy()
        }
    }
}

/*
    原来的 遮罩模型，走 GuideDestroy，新版的 无遮罩 在这里 destroy
 */
const GuideDestroyExtend = selector => {
    const { options, destroy } = _GuideOptions[selector]
    const { mode } = options
    if (mode === GuideMode.OVERLAY) {
        // 兼容老逻辑
        GuideDestroy()
    } else if (mode === GuideMode.NO_OVERLAY) {
        if (destroy) {
            destroy()
        }
    }
    delete _GuideOptions[selector]
}

const guideKey = 'GYL-GUIDE'
const maxLength = 20

export const checkGuideStorage = val => {
    return checkCookiesStorage({ val, key: guideKey })
}

export const setGuideStorage = val => {
    return setCookiesStorage({ val, key: guideKey, maxLength })
}

// Guide 现在有两种形态
export const GuideMode = {
    OVERLAY: 'overlay', // 有遮罩（默认）
    NO_OVERLAY: 'noOverlay' // 无遮罩
}
const allowPlacement = [
    'leftBottom',
    'rightBottom',
    'rightTop',
    'rightCenter',
    'leftCenter',
    'bottomRight',
    'bottomLeft',
    'topCenter'
]

const _GuideOptions = {}
export function destroyNoOverLayGuideBySelector(selector) {
    let o = _GuideOptions[selector]
    if (o) {
        if (o.destroy) {
            o.destroy()
        }
        delete _GuideOptions[selector]
    }
}
function hasSameMode(mode) {
    let found = Object.keys(_GuideOptions).filter(s => {
        return _GuideOptions[s].options.mode === mode
    })
    return !!found.length
}

export const showGuide = (selector, options) => {
    options.mode = options.mode || GuideMode.OVERLAY
    const { placement = 'leftBottom', delay = 0, getCustomContainer = getRootDom } = options
    if (allowPlacement.indexOf(placement) === -1) {
        throw new Error('placement is not supported')
    } else if (!parseInt(delay, 10)) {
        throw new Error('delay must be number')
    } else {
        const openGuide = (setFinishFlag = () => {}) => {
            destroyNoOverLayGuideBySelector(selector) // 场景：兼容了需要等待相关界面异步渲染完成，更新 offset。 业务方设置合理的delay，并且再次发起调用即可
            setTimeout(() => {
                if (options.noStop || !hasSameMode(options.mode)) {
                    try {
                        const { destroy } = createAPI(GuideDom, {
                            selector,
                            options,
                            getCustomContainer,
                            root: getCustomContainer,
                            setFinishFlag
                        })
                        // 兼容现有逻辑
                        if (options.mode === GuideMode.OVERLAY) {
                            _GuideDestroyFns.push(destroy)
                        }
                        // 收集所有调用
                        _GuideOptions[selector] = { options, destroy }
                    } catch (error) {
                        console.error('catchError', error)
                        captureException(error, {
                            anchor: '引导'
                        })
                    }
                }
            }, delay)
        }

        if (options.mode === GuideMode.OVERLAY) {
            const { setFinishFlag, remove } = register({
                needComplete: true,
                immediately: true,
                id: selector,
                execContent: () => {
                    openGuide(setFinishFlag, remove)
                }
            })
        } else {
            openGuide()
        }
    }
}

function calcOffset(element, widthSelector, mode) {
    if (element) {
        let offset = getOffset(element, mode)
        if (widthSelector) {
            const widthElement = document.querySelector(widthSelector)
            if (widthElement) {
                const { width } = getOffset(widthElement, mode)
                offset.width = width
            }
        }
        return offset
    }
    return null
}

const SCROLL_EVENT = 'scroll'
const scrollHeight = 300
const GuideDom = ({ selector, options, visible: propsVis, onCancel, setFinishFlag }) => {
    const {
        title,
        content,
        storageValue,
        scroll,
        placement = 'leftBottom',
        widthSelector = '',
        noScroll = false,
        lockPage = false,
        saveLocation = 'cookie',
        okText = '我知道了',
        introStyle = {},
        overlayStyle = {},
        onOk = () => {},
        root,
        // 目标element 不在可视化区域内，不展示提示
        hideGuideWhenElementNotInViewport = false,
        isClose = false,
        mode = GuideMode.OVERLAY, // 默认遮罩模式，同时支持不同mode同时展示
        maskCloseAble = false, // 遮罩模式下，是否点击遮罩层关闭
        watchElementScrollForUpdateOffset: watchElementScroll // 监听指定元素，重新计算offset
    } = options
    const rootElement = getRootDom()
    const element = document.querySelector(selector)
    const [visible, setVis] = useState(() => {
        if (hideGuideWhenElementNotInViewport) {
            return false
        }
        return propsVis
    })
    if (!element) {
        setFinishFlag(true)
    }
    // 目标element 不在可视化区域内，不展示提示
    const [, ratio] = useInViewport(() => document.querySelector(selector), {
        threshold: inViewportThreshold,
        root: root || getRootDom()
    })

    const noOverlay = mode === GuideMode.NO_OVERLAY
    const [offset, setOffset] = useState(calcOffset(element, widthSelector, mode))
    const handleScroll = useCallback(() => {
        setOffset(calcOffset(element, widthSelector, mode))
    }, [watchElementScroll])

    const initScroll = () => {
        if (scroll && offset && offset.top > scrollHeight) {
            const maxHeight = rootElement.scrollHeight - rootElement.offsetHeight
            let scrollTop = offset.top - scrollHeight
            if (maxHeight < scrollTop) {
                scrollTop = maxHeight
            }
            try {
                if (!noScroll) {
                    rootElement.scroll &&
                        rootElement.scroll({
                            top: scrollTop,
                            behavior: 'smooth'
                        })
                }
            } catch (error) {
                console.error('catchError', error)
                captureException(error, { anchor: 'GuideDom' })
            }
        }
    }

    useEffect(() => {
        // 遮罩模式下 判断可见区域，不可见不展示

        if ((ratio || 0) < ratioLimit && !noOverlay && hideGuideWhenElementNotInViewport) {
            rootElement.style.overflowX = 'auto'
            rootElement.style.overflowY = 'auto'
            document.documentElement.style.overflow = 'auto'
            return
        }

        if (!visible && hideGuideWhenElementNotInViewport) {
            setVis(true)
        }
        initScroll()
        if (!watchElementScroll) {
            return
        }
        watchElementScroll.addEventListener(SCROLL_EVENT, handleScroll)
        return () => {
            watchElementScroll.removeEventListener(SCROLL_EVENT, handleScroll)
        }
    }, [watchElementScroll])

    if (visible && element && rootElement) {
        if (lockPage) {
            rootElement.style.overflowX = 'hidden'
            rootElement.style.overflowY = 'hidden'
            document.documentElement.style.overflow = 'hidden'
        }

        const guideStyle = getGuideStyle(offset, placement)
        const handelClick = (...args) => {
            setFinishFlag(true)
            if (storageValue) {
                switch (saveLocation) {
                    case 'cookie':
                        setGuideStorage(storageValue)
                        break
                    case 'storage':
                        localStorage.setItem(storageValue, true)
                        break
                    default:
                        break
                }
            }

            if (lockPage) {
                rootElement.style.overflowX = 'auto'
                rootElement.style.overflowY = 'auto'
                document.documentElement.style.overflow = 'auto'
            }
            onCancel(...args)
            GuideDestroyExtend(selector)
        }
        const overlayHeight = { height: rootElement.scrollHeight }
        const introOffsetStyle = { ...offset, ...introStyle }

        return (
            <>
                {!noOverlay ? (
                    <div
                        style={{ ...overlayHeight, ...overlayStyle }}
                        onClick={() => {
                            if (maskCloseAble) {
                                handelClick()
                                onOk()
                            }
                        }}
                        className={styles.overlay}
                    />
                ) : null}
                <div
                    style={introOffsetStyle}
                    className={classNames(styles.introjs, noOverlay ? styles.introjsNoOverlay : '')}
                >
                    <div style={guideStyle} className={classNames(styles.guide, styles[placement])}>
                        {isClose && (
                            <div className={styles.close} onClick={handelClick}>
                                <CloseOutlined />
                            </div>
                        )}
                        <div className={styles.title}>{title}</div>
                        <div className={styles.content}>{content}</div>
                        <div className={styles.btnWrap}>
                            <a
                                onClick={() => {
                                    handelClick()
                                    onOk()
                                }}
                            >
                                {okText}
                            </a>
                        </div>
                    </div>
                </div>
            </>
        )
    } else {
        return null
    }
}

const getGuideStyle = (offset, placement) => {
    const { height, width } = offset
    let style = {}
    if (placement === 'leftBottom') {
        style = {
            left: 0,
            top: height + 20
        }
    } else if (placement === 'bottomRight') {
        style = {
            right: 0,
            top: height + 20
        }
    } else if (placement === 'rightBottom') {
        style = {
            // eslint-disable-next-line no-magic-numbers
            right: -276 - 20,
            bottom: 0
        }
    } else if (placement === 'rightTop') {
        style = {
            right: 0,
            bottom: height + 20
        }
    } else if (placement === 'rightCenter') {
        style = {
            left: width + 20,
            top: '50%',
            transform: 'translateY(-50%)'
        }
    } else if (placement === 'leftCenter') {
        style = {
            left: -276,
            top: '50%',
            transform: 'translateY(-50%)'
        }
    } else if (placement === 'bottomLeft') {
        style = {
            right: '-100px',
            top: 40
        }
    } else if (placement === 'topCenter') {
        style = {
            bottom: height + 20,
            left: '50%',
            transform: 'translateX(-50%)'
        }
    }

    return style
}

/**
 * Get an element position on the page relative to another element (or body)
 * Thanks to `meouw`: http://stackoverflow.com/a/442474/375966
 *
 * @api private
 * @method getOffset
 * @param {Object} element
 * @param {Object} relativeEl
 * @returns Element's position info
 */
function getOffset(element, mode) {
    // root
    const body = getRootDom()
    const docEl = document.documentElement
    let scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop
    let scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft

    const x = element?.getBoundingClientRect()

    let obj = {
        width: x.width,
        height: x.height
    }
    // 无遮罩时，guide为fixed与body滚动无关
    if (mode === GuideMode.NO_OVERLAY) {
        obj.top = x.top
        obj.width = 1 // 不要遮挡 element
        obj.left = x.left + x.width
    }

    return { top: x.top + scrollTop, left: x.left + scrollLeft, ...obj }
}
